import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import Grid from "@material-ui/core/Grid";
import { EMPLOYEE_ACTION } from "../../redux/action";
import API from "../../utils/api";
import {
  dateFormatter,
  Toaster,
  getUserTypeID,
  dropdownNameWithEmail,
} from "../../utils/common";
import { renderMeetingLink } from "../../utils/meetingLink";
import "./hrms.scss";
import { hasHrmsAdminAccess } from "../../utils/hrmsAccess";
import {
  normalizeEmployeeTeam,
  visibleTeamsForEmail,
} from "../../utils/leaveTeamApprovers";
import { Table, Labelbox, Button } from "../../components";
import { Edit, Close as CloseIcon, Save } from "@material-ui/icons";
import { header as employeeHeader } from "../employee/defaultState";
import Leaves from "../leaves";
import PerformanceIndex from "../performance_index";
import WorkingHours from "../working_hours";
import HrmsDashboard from "./HrmsDashboard";
import { maybeEncryptEmployeePiiFields } from "../../utils/employeePiiTransport";
import {
  buildEmployeePartialPayload,
  resolveEmployeeUuid,
} from "../../utils/employeeProfileUpdate";
import PhoneInput from "../../components/PhoneInput";

/** Fields allowed on POST /employee/updateEmployeeById/:id (snake_case body). */
const HRMS_UPDATABLE_KEYS = new Set([
  "first_name",
  "last_name",
  "email",
  "phone",
  "role_id",
  "manager_id",
  "status",
  "doj",
  "designation",
  "branch_office",
  "department",
  "location",
  "employment_type",
  "date_of_birth",
  "gender",
  "nationality",
  "marital_status",
  "blood_group",
  "personal_phone",
  "personal_email",
  "emergency_contact_name",
  "emergency_contact_number",
  "present_address",
  "permanent_address",
  "address_1",
  "address_2",
  "country_id",
  "state_id",
  "city_id",
  "zipcode",
  "uan",
  "pan",
  "aadhaar",
  "company_employee_id",
]);

const initialFromEmployee = (emp) => {
  const name = `${emp?.first_name || ""} ${emp?.last_name || ""}`.trim();
  if (name) return name.charAt(0).toUpperCase();
  const email = String(emp?.email || "").trim();
  if (email) return email.charAt(0).toUpperCase();
  return "?";
};

export default function HRMS() {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("Profile");
  /** Top HRMS navigation: My Overview / All Employees vs widget Dashboard */
  const [hrmsTopTab, setHrmsTopTab] = useState("overview");
  const [viewAll, setViewAll] = useState(false);
  const [showEmployeeOverview, setShowEmployeeOverview] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedEmpId, setSelectedEmpId] = useState(null);
  const [selectedEmpForOverview, setSelectedEmpForOverview] = useState(null);
  const [overviewActiveTab, setOverviewActiveTab] = useState("Profile");
  const [isEmployeeEditMode, setIsEmployeeEditMode] = useState(false);
  const [employeeEditedData, setEmployeeEditedData] = useState({});
  const preserveNextEmployeeEditModeRef = useRef(false);
  const hrmsEditBaselineRef = useRef(null);
  const [savingProfile, setSavingProfile] = useState(false);
  // Keep My Overview profile data local so other tabs (Leaves/Login/etc.)
  // don't overwrite Redux employee.getEmployee with manager details.
  const [profileData, setProfileData] = useState({});
  const { employee } = useSelector((state) => state);
  const USER_DATA = JSON.parse(localStorage.getItem("USER_DATA") || "{}");
  const loggedEmployeeId =
    USER_DATA?.employee_id || localStorage.getItem("user_id");

  // determine roles (best-effort using common keys)
  const userRole =
    (USER_DATA &&
      (USER_DATA.role || USER_DATA.user_role || USER_DATA.role_name)) ||
    "";
  const storedUserRole = (
    localStorage.getItem("user_role") || ""
  ).toLowerCase();
  const isAdmin =
    hasHrmsAdminAccess(loggedEmployeeId) ||
    USER_DATA?.is_admin ||
    storedUserRole === "admin" ||
    String(userRole).toLowerCase() === "admin";
  const isManager =
    !isAdmin &&
    (storedUserRole === "manager" ||
      String(userRole).toLowerCase() === "manager" ||
      USER_DATA?.is_manager);

  const userTypeId = getUserTypeID();
  const isHrmsClient = userTypeId === 4;
  const isHrmsAdmin = userTypeId === 1 || hasHrmsAdminAccess(loggedEmployeeId);
  const teamLeaveScope = visibleTeamsForEmail(USER_DATA?.email);
  const isTeamLeaveApprover = !isAdmin && teamLeaveScope.length > 0;

  useEffect(() => {
    if (isHrmsClient) {
      setHrmsTopTab("overview");
    }
  }, [isHrmsClient]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!loggedEmployeeId) return;
      try {
        const payload = await dispatch(
          EMPLOYEE_ACTION.getEmployee(loggedEmployeeId),
        ).unwrap();
        const normalized = Array.isArray(payload)
          ? payload[0] || {}
          : payload || {};
        if (!cancelled && normalized && typeof normalized === "object") {
          setProfileData(normalized);
        }
      } catch (_e) {
        // Ignore; UI can render with empty state until a successful fetch.
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [dispatch, loggedEmployeeId]);

  useEffect(() => {
    dispatch(EMPLOYEE_ACTION.getActiveEmployees());
  }, [dispatch]);

  const data = profileData;

  const managerNameMap = useMemo(() => {
    const map = {};
    (employee?.activeEmployees || []).forEach((emp) => {
      const fullName = [emp?.first_name, emp?.last_name]
        .filter(Boolean)
        .join(" ")
        .trim();
      map[String(emp?.employee_id || emp?._id || emp?.id)] =
        fullName || emp?.email || "-";
    });
    return map;
  }, [employee?.activeEmployees]);

  // Manager dropdown: admins (role_id === 1) and managers (role_id === 2)
  const managerDropdown = useMemo(() => {
    if (!employee?.activeEmployees || !Array.isArray(employee.activeEmployees))
      return [];
    return employee.activeEmployees
      .filter((data) => data.role_id === 1 || data.role_id === 2)
      .map((data) => ({
        id: data.employee_id || data._id,
        value: dropdownNameWithEmail(data),
      }));
  }, [employee?.activeEmployees]);

  const branchOfficeDropdown = useMemo(
    () => [
      { id: "Sunnyvale - USA", value: "Sunnyvale - USA" },
      { id: "Pleasanton - USA", value: "Pleasanton - USA" },
      { id: "Bangalore - India", value: "Bangalore - India" },
      { id: "Sirsi - India", value: "Sirsi - India" },
    ],
    [],
  );

  const filteredActiveEmployees = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const currentUserId = String(loggedEmployeeId || "");
    const list = (employee?.activeEmployees || []).filter((emp) => {
      const onTeam =
        isTeamLeaveApprover &&
        teamLeaveScope.includes(normalizeEmployeeTeam(emp?.team));
      if (isAdmin) return true;
      if (isTeamLeaveApprover && !isManager) return onTeam;
      if (!isManager) return true;
      const directReport =
        String(emp?.manager_id || "") === currentUserId &&
        String(emp?.employee_id || emp?._id || emp?.id || "") !== currentUserId;
      return directReport || onTeam;
    });

    if (!query) return list;

    return list.filter((emp) => {
      const fullName =
        `${emp?.first_name || ""} ${emp?.last_name || ""}`.toLowerCase();
      const email = String(emp?.email || "").toLowerCase();
      const designation = String(
        emp?.designation || emp?.role || "",
      ).toLowerCase();
      const phone = String(emp?.phone || "").toLowerCase();
      const empId = String(
        emp?.employeeID || emp?.employee_id || "",
      ).toLowerCase();

      return (
        fullName.includes(query) ||
        email.includes(query) ||
        designation.includes(query) ||
        phone.includes(query) ||
        empId.includes(query)
      );
    });
  }, [employee?.activeEmployees, searchQuery, isAdmin, isManager, isTeamLeaveApprover, teamLeaveScope, loggedEmployeeId]);

  const displayValue = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    return value;
  };

  const displayMeetingLink = (value) => renderMeetingLink(value);

  const getEmployeeScopeId = (empData) =>
    String(
      empData?.employeeID ||
        empData?.employee_id ||
        empData?._id ||
        empData?.id ||
        "",
    ).trim();

  const getManagerDisplayName = (managerId) => {
    if (managerId === null || managerId === undefined || managerId === "")
      return "-";
    return (
      managerNameMap[String(managerId)] ||
      displayValue(data.manager_name || data.manager)
    );
  };

  const handleEditClick = (empData) => {
    hrmsEditBaselineRef.current = { ...(empData || {}) };
    setEmployeeEditedData({ ...(empData || {}) });
    setIsEmployeeEditMode(true);
  };

  const handleEditCancel = () => {
    setIsEmployeeEditMode(false);
    setEmployeeEditedData({});
    hrmsEditBaselineRef.current = null;
  };

  const handleHrmsProfileSave = async (contextRow) => {
    const edited = employeeEditedData;
    const employeeId = resolveEmployeeUuid(edited, contextRow);
    if (!employeeId) {
      Toaster.error("Missing employee id; cannot save.");
      return;
    }
    const baseline = hrmsEditBaselineRef.current || {};
    const partial = buildEmployeePartialPayload(edited, baseline);
    if (
      Object.prototype.hasOwnProperty.call(partial, "role_id") &&
      Number(partial.role_id) === 1
    ) {
      partial.manager_id = null;
    }
    if (Object.keys(partial).length === 0) {
      Toaster.warning("No changes to save.");
      return;
    }

    setSavingProfile(true);
    try {
      const safePartial = await maybeEncryptEmployeePiiFields(partial);
      const response = await API.post(
        `employee/updateEmployeeById/${employeeId}`,
        safePartial,
      );
      const body = response?.data || {};
      if (response?.status === 200 && body.status === 1) {
        Toaster.success(body.message || "Profile updated successfully.");
        dispatch(EMPLOYEE_ACTION.getActiveEmployees());
        if (showEmployeeOverview && selectedEmpForOverview) {
          try {
            const refreshed = await API.get(
              `employee/getEmployeeById/${employeeId}`,
            );
            const rBody = refreshed.data || {};
            if (refreshed.status === 200 && rBody.status === 1 && rBody.data) {
              setSelectedEmpForOverview(rBody.data);
            }
          } catch (_e) {
            /* keep existing overview row if refresh fails */
          }
        } else {
          try {
            const refreshed = await dispatch(
              EMPLOYEE_ACTION.getEmployee(loggedEmployeeId),
            ).unwrap();
            const normalized = Array.isArray(refreshed)
              ? refreshed[0] || {}
              : refreshed || {};
            setProfileData(normalized);
          } catch (_e) {
            /* ignore; next fetch will repopulate profileData */
          }
        }
        handleEditCancel();
      } else {
        const msg = body.message || body.error || "Failed to update profile.";
        if (String(msg).includes("EMPLOYEE_PII_SECRET")) {
          Toaster.error(
            "Saving identity documents is temporarily unavailable; contact admin.",
          );
        } else {
          Toaster.error(msg);
        }
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to update profile.";
      if (String(msg).includes("EMPLOYEE_PII_SECRET")) {
        Toaster.error(
          "Saving identity documents is temporarily unavailable; contact admin.",
        );
      } else {
        Toaster.error(msg);
      }
    } finally {
      setSavingProfile(false);
    }
  };

  const handleEmployeeFieldChange = (field, value) => {
    setEmployeeEditedData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const renderEditableInfoValue = (field, value, config = {}) => {
    if (!isEmployeeEditMode) {
      if (config.type === "date") {
        return value ? dateFormatter(value) : "-";
      }
      if (config.type === "select" && field === "manager_id") {
        return managerNameMap[String(value)] || displayValue(value) || "-";
      }
      if (field === "meeting_link") {
        return displayMeetingLink(value);
      }
      return displayValue(value);
    }

    const { type = "text", dropdown = [] } = config;
    const currentValue =
      field in employeeEditedData ? employeeEditedData[field] : (value ?? "");

    if (
      field === "phone" ||
      field === "personal_phone" ||
      field === "emergency_contact_number"
    ) {
      return (
        <PhoneInput
          value={currentValue ?? ""}
          onChange={(val) => handleEmployeeFieldChange(field, val)}
          labelname=""
        />
      );
    }

    return (
      <Labelbox
        type={
          type === "date" ? "datepicker" : type === "select" ? "select" : "text"
        }
        labelname=""
        value={currentValue ?? ""}
        changeData={(val) => handleEmployeeFieldChange(field, val)}
        dropdown={dropdown}
      />
    );
  };

  // Role-based edit for own profile (My Overview)
  const canEditSectionProfile = (sectionName) => {
    if (isAdmin) return true; // Admin can edit all
    // Non-admin can only edit these sections
    return (
      sectionName === "personalDetails" ||
      sectionName === "contactDetails" ||
      sectionName === "identityInfo"
    );
  };

  const renderEditableInfoValueWithRole = (
    field,
    value,
    sectionName,
    config = {},
  ) => {
    const sectionEditable = canEditSectionProfile(sectionName);
    const fieldEditable = sectionEditable && isEmployeeEditMode;

    if (!fieldEditable) {
      if (config.type === "date") {
        return value ? dateFormatter(value) : "-";
      }
      if (config.type === "select" && field === "manager_id") {
        return managerNameMap[String(value)] || displayValue(value) || "-";
      }
      if (field === "meeting_link") {
        return displayMeetingLink(value);
      }
      return displayValue(value);
    }

    const { type = "text", dropdown = [] } = config;
    const currentValue =
      field in employeeEditedData ? employeeEditedData[field] : (value ?? "");

    if (
      field === "phone" ||
      field === "personal_phone" ||
      field === "emergency_contact_number"
    ) {
      return (
        <PhoneInput
          value={currentValue ?? ""}
          onChange={(val) => handleEmployeeFieldChange(field, val)}
          labelname=""
        />
      );
    }

    return (
      <Labelbox
        type={
          type === "date" ? "datepicker" : type === "select" ? "select" : "text"
        }
        labelname=""
        value={currentValue ?? ""}
        changeData={(val) => handleEmployeeFieldChange(field, val)}
        dropdown={dropdown}
      />
    );
  };

  useEffect(() => {
    if (preserveNextEmployeeEditModeRef.current) {
      preserveNextEmployeeEditModeRef.current = false;
      return;
    }
    setIsEmployeeEditMode(false);
    setEmployeeEditedData({});
  }, [
    viewAll,
    showEmployeeOverview,
    activeTab,
    overviewActiveTab,
    selectedEmpForOverview,
  ]);

  useEffect(() => {
    if (selectedEmpForOverview) {
      setOverviewActiveTab("Profile");
    }
  }, [selectedEmpForOverview]);

  // Helper function to find employee by ID
  const findEmployeeById = (empId) => {
    if (!empId) return null;
    return (employee?.activeEmployees || []).find(
      (e) => String(e?.employee_id || e?._id || e?.id) === String(empId),
    );
  };

  const handleAllEmployeesAction = (type, row, actionIndex) => {
    if (type !== "edit") return;
    const selectedData =
      row?._rawData || filteredActiveEmployees?.[actionIndex];
    if (!selectedData) return;

    preserveNextEmployeeEditModeRef.current = true;
    hrmsEditBaselineRef.current = { ...(selectedData || {}) };
    setSelectedEmpForOverview(selectedData);
    setViewAll(true);
    setShowEmployeeOverview(true);
    setOverviewActiveTab("Profile");
    setEmployeeEditedData({ ...(selectedData || {}) });
    setIsEmployeeEditMode(true);
  };

  // Render employee overview (same design as Profile tab)
  const renderEmployeeOverview = (empData) => {
    if (!empData) return null;
    const d = empData;
    const employeeScopeId = getEmployeeScopeId(d);
    const canEditEveryFieldInOverview =
      isAdmin ||
      (isManager &&
        String(d?.manager_id || "") === String(loggedEmployeeId || ""));

    // Determine if current viewer can edit all sections (only admins)
    const isViewerAdmin = canEditEveryFieldInOverview;

    // Helper to check if a section can be edited based on viewer role
    const canEditSection = (sectionName) => {
      if (isViewerAdmin) return true; // Admin can edit all
      // Non-admin can only edit these sections
      return (
        sectionName === "personalDetails" ||
        sectionName === "contactDetails" ||
        sectionName === "identityInfo"
      );
    };

    // Modified renderEditableInfoValue for overview with role-based permissions
    const renderEditableInfoValueOverview = (
      field,
      value,
      sectionName,
      config = {},
    ) => {
      const sectionEditable = canEditSection(sectionName);
      const fieldEditable = sectionEditable && isEmployeeEditMode;

      if (!fieldEditable) {
        if (config.type === "date") {
          return value ? dateFormatter(value) : "-";
        }
        if (config.type === "select" && field === "manager_id") {
          return managerNameMap[String(value)] || displayValue(value) || "-";
        }
        if (field === "meeting_link") {
          return displayMeetingLink(value);
        }
        return displayValue(value);
      }

      const { type = "text", dropdown = [] } = config;
      const currentValue =
        field in employeeEditedData ? employeeEditedData[field] : (value ?? "");

      if (
        field === "phone" ||
        field === "personal_phone" ||
        field === "emergency_contact_number"
      ) {
        return (
          <PhoneInput
            value={currentValue ?? ""}
            onChange={(val) => handleEmployeeFieldChange(field, val)}
            labelname=""
          />
        );
      }

      return (
        <Labelbox
          type={
            type === "date"
              ? "datepicker"
              : type === "select"
                ? "select"
                : "text"
          }
          labelname=""
          value={currentValue ?? ""}
          changeData={(val) => handleEmployeeFieldChange(field, val)}
          dropdown={dropdown}
        />
      );
    };
    return (
      <div className="profile-content">
        <div className="action-tabs">
          {[
            "Profile",
            // "Login",
            "Leave",
            "Performance Index",
            // "Related Data",
            // "Resignation",
          ].map((tab) => (
            <button
              key={tab}
              className={`action-tab ${overviewActiveTab === tab ? "active" : ""}`}
              onClick={() => setOverviewActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        {overviewActiveTab === "Leave" ? (
          <Leaves
            scopeEmployeeId={employeeScopeId}
            defaultActiveTab="list"
            readOnlyView={true}
            hideApplyTab
            hideFilters
          />
        ) : overviewActiveTab === "Performance Index" ? (
          <PerformanceIndex
            scopeEmployeeId={employeeScopeId}
            hideSearchControls={true}
          />
        ) : overviewActiveTab === "Profile" ? (
          <>
            <div
              style={{
                marginBottom: "16px",
                display: "flex",
                justifyContent: "flex-end",
                width: "100%",
                gap: "8px",
              }}
            >
              <Button
                type="button"
                icon={isEmployeeEditMode ? <CloseIcon /> : <Edit />}
                className="section-edit-btn"
                text={isEmployeeEditMode ? "Cancel" : "Edit"}
                handleClick={() =>
                  isEmployeeEditMode ? handleEditCancel() : handleEditClick(d)
                }
              />
              {isEmployeeEditMode && (
                <Button
                  type="button"
                  className="section-edit-btn"
                  text={savingProfile ? "Saving…" : "Update"}
                  icon={<Save />}
                  disable={savingProfile}
                  handleClick={() => handleHrmsProfileSave(d)}
                />
              )}
            </div>
            {/* Basic Information */}
            <div className="info-section">
              <div className="section-header-row">
                <h3 className="section-header">Basic information</h3>
              </div>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <div className="info-label">Employee ID</div>
                  <div className="info-value">
                    {renderEditableInfoValueOverview(
                      "company_employee_id",
                      d.company_employee_id || d.employeeID,
                      "basicInfo",
                    )}
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className="info-label">Email ID</div>
                  <div className="info-value">
                    {renderEditableInfoValueOverview(
                      "email",
                      d.email,
                      "basicInfo",
                    )}
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className="info-label">First Name</div>
                  <div className="info-value">
                    {renderEditableInfoValueOverview(
                      "first_name",
                      d.first_name,
                      "basicInfo",
                    )}
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className="info-label">Last Name</div>
                  <div className="info-value">
                    {renderEditableInfoValueOverview(
                      "last_name",
                      d.last_name,
                      "basicInfo",
                    )}
                  </div>
                </Grid>
              </Grid>
            </div>

            {/* Work Information */}
            <div className="info-section">
              <h3 className="section-header">Work information</h3>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <div className="info-label">Department</div>
                  <div className="info-value">
                    {renderEditableInfoValueOverview(
                      "department",
                      d.department,
                      "workInfo",
                    )}
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className="info-label">Location</div>
                  <div className="info-value">
                    {renderEditableInfoValueOverview(
                      "location",
                      d.location,
                      "workInfo",
                    )}
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className="info-label">Reporting Manager</div>
                  <div className="info-value">
                    {renderEditableInfoValueOverview(
                      "manager_id",
                      d.manager_id,
                      "workInfo",
                      { type: "select", dropdown: managerDropdown },
                    )}
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className="info-label">Employment Type</div>
                  <div className="info-value">
                    {renderEditableInfoValueOverview(
                      "employment_type",
                      d.employment_type,
                      "workInfo",
                    )}
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className="info-label">Date of Joining</div>
                  <div className="info-value">
                    {renderEditableInfoValueOverview("doj", d.doj, "workInfo", {
                      type: "date",
                    })}
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className="info-label">Designation</div>
                  <div className="info-value">
                    {renderEditableInfoValueOverview(
                      "designation",
                      d.designation,
                      "workInfo",
                    )}
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className="info-label">Branch Office</div>
                  <div className="info-value">
                    {renderEditableInfoValueOverview(
                      "branch_office",
                      d.branch_office,
                      "workInfo",
                      { type: "select", dropdown: branchOfficeDropdown },
                    )}
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className="info-label">Work Phone</div>
                  <div className="info-value">
                    {renderEditableInfoValueOverview(
                      "phone",
                      d.phone,
                      "workInfo",
                    )}
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className="info-label">Meeting Link</div>
                  <div className="info-value">
                    {renderEditableInfoValueOverview(
                      "meeting_link",
                      d.meeting_link,
                      "workInfo",
                    )}
                  </div>
                </Grid>
              </Grid>
            </div>

            {/* Personal Details */}
            <div className="info-section">
              <h3 className="section-header">Personal Details</h3>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <div className="info-label">Date of Birth</div>
                  <div className="info-value">
                    {renderEditableInfoValueOverview(
                      "date_of_birth",
                      d.date_of_birth,
                      "personalDetails",
                      {
                        type: "date",
                      },
                    )}
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className="info-label">Gender</div>
                  <div className="info-value">
                    {renderEditableInfoValueOverview(
                      "gender",
                      d.gender,
                      "personalDetails",
                    )}
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className="info-label">Nationality</div>
                  <div className="info-value">
                    {renderEditableInfoValueOverview(
                      "nationality",
                      d.nationality,
                      "personalDetails",
                    )}
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className="info-label">Marital Status</div>
                  <div className="info-value">
                    {renderEditableInfoValueOverview(
                      "marital_status",
                      d.marital_status,
                      "personalDetails",
                    )}
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className="info-label">Blood Group</div>
                  <div className="info-value">
                    {renderEditableInfoValueOverview(
                      "blood_group",
                      d.blood_group,
                      "personalDetails",
                    )}
                  </div>
                </Grid>
              </Grid>
            </div>

            {/* Contact Details */}
            <div className="info-section">
              <h3 className="section-header">Contact Details</h3>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <div className="info-label">Personal Phone</div>
                  <div className="info-value">
                    {renderEditableInfoValueOverview(
                      "personal_phone",
                      d.personal_phone,
                      "contactDetails",
                    )}
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className="info-label">Personal Email Address</div>
                  <div className="info-value">
                    {renderEditableInfoValueOverview(
                      "personal_email",
                      d.personal_email,
                      "contactDetails",
                    )}
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className="info-label">Emergency Contact Name</div>
                  <div className="info-value">
                    {renderEditableInfoValueOverview(
                      "emergency_contact_name",
                      d.emergency_contact_name,
                      "contactDetails",
                    )}
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className="info-label">Emergency Contact No.</div>
                  <div className="info-value">
                    {renderEditableInfoValueOverview(
                      "emergency_contact_number",
                      d.emergency_contact_number,
                      "contactDetails",
                    )}
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className="info-label">Present Address</div>
                  <div className="info-value">
                    {renderEditableInfoValueOverview(
                      "present_address",
                      d.present_address,
                      "contactDetails",
                    )}
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className="info-label">Permanent Address</div>
                  <div className="info-value">
                    {renderEditableInfoValueOverview(
                      "permanent_address",
                      d.permanent_address,
                      "contactDetails",
                    )}
                  </div>
                </Grid>
              </Grid>
            </div>

            {/* Identity Information */}
            <div className="info-section">
              <h3 className="section-header">Identity Information</h3>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <div className="info-label">UAN</div>
                  <div className="info-value">
                    {renderEditableInfoValueOverview(
                      "uan",
                      d.uan,
                      "identityInfo",
                    )}
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className="info-label">PAN</div>
                  <div className="info-value">
                    {renderEditableInfoValueOverview(
                      "pan",
                      d.pan,
                      "identityInfo",
                    )}
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className="info-label">Aadhaar</div>
                  <div className="info-value">
                    {renderEditableInfoValueOverview(
                      "aadhaar",
                      d.aadhaar,
                      "identityInfo",
                    )}
                  </div>
                </Grid>
              </Grid>
            </div>
          </>
        ) : null}
      </div>
    );
  };

  //   const renderOverview = (empData) => {
  //     const d = empData || {};
  //     return (
  //       <div className="profile-content">
  //         {/* Basic Information */}
  //         <div className="info-section">
  //           <h3 className="section-header">Basic information</h3>
  //           <Grid container spacing={3}>
  //             <Grid item xs={12} sm={6}>
  //               <div className="info-label">Employee ID</div>
  //               <div className="info-value">
  //                 {displayValue(d.employeeID || d.employee_id)}
  //               </div>
  //             </Grid>
  //             <Grid item xs={12} sm={6}>
  //               <div className="info-label">Email ID</div>
  //               <div className="info-value">{displayValue(d.email)}</div>
  //             </Grid>
  //             <Grid item xs={12} sm={6}>
  //               <div className="info-label">First Name</div>
  //               <div className="info-value">{displayValue(d.first_name)}</div>
  //             </Grid>
  //             <Grid item xs={12} sm={6}>
  //               <div className="info-label">Last Name</div>
  //               <div className="info-value">{displayValue(d.last_name)}</div>
  //             </Grid>
  //           </Grid>
  //         </div>

  //         {/* Work Information */}
  //         <div className="info-section">
  //           <h3 className="section-header">Work information</h3>
  //           <Grid container spacing={3}>
  //             <Grid item xs={12} sm={6}>
  //               <div className="info-label">Department</div>
  //               <div className="info-value">{displayValue(d.department)}</div>
  //             </Grid>
  //             <Grid item xs={12} sm={6}>
  //               <div className="info-label">Location</div>
  //               <div className="info-value">{displayValue(d.location)}</div>
  //             </Grid>
  //             <Grid item xs={12} sm={6}>
  //               <div className="info-label">Reporting Manager</div>
  //               <div className="info-value">
  //                 {managerNameMap[String(d.manager_id)] ||
  //                   displayValue(d.manager_name || d.manager)}
  //               </div>
  //             </Grid>
  //             <Grid item xs={12} sm={6}>
  //               <div className="info-label">Employment Type</div>
  //               <div className="info-value">
  //                 {displayValue(d.employment_type)}
  //               </div>
  //             </Grid>
  //             <Grid item xs={12} sm={6}>
  //               <div className="info-label">Date of Joining</div>
  //               <div className="info-value">
  //                 {d.doj ? dateFormatter(d.doj) : "-"}
  //               </div>
  //             </Grid>
  //             <Grid item xs={12} sm={6}>
  //               <div className="info-label">Designation</div>
  //               <div className="info-value">{displayValue(d.designation)}</div>
  //             </Grid>
  //             <Grid item xs={12} sm={6}>
  //               <div className="info-label">Branch Office</div>
  //               <div className="info-value">{displayValue(d.branch_office)}</div>
  //             </Grid>
  //           </Grid>
  //         </div>

  //         {/* Personal Details */}
  //         <div className="info-section">
  //           <h3 className="section-header">Personal Details</h3>
  //           <Grid container spacing={3}>
  //             <Grid item xs={12} sm={6}>
  //               <div className="info-label">Date of Birth</div>
  //               <div className="info-value">
  //                 {d.date_of_birth ? dateFormatter(d.date_of_birth) : "-"}
  //               </div>
  //             </Grid>
  //             <Grid item xs={12} sm={6}>
  //               <div className="info-label">Gender</div>
  //               <div className="info-value">{displayValue(d.gender)}</div>
  //             </Grid>
  //             <Grid item xs={12} sm={6}>
  //               <div className="info-label">Nationality</div>
  //               <div className="info-value">{displayValue(d.nationality)}</div>
  //             </Grid>
  //             <Grid item xs={12} sm={6}>
  //               <div className="info-label">Marital Status</div>
  //               <div className="info-value">{displayValue(d.marital_status)}</div>
  //             </Grid>
  //             <Grid item xs={12} sm={6}>
  //               <div className="info-label">Blood Group</div>
  //               <div className="info-value">{displayValue(d.blood_group)}</div>
  //             </Grid>
  //           </Grid>
  //         </div>

  //         {/* Contact Details */}
  //         <div className="info-section">
  //           <h3 className="section-header">Contact Details</h3>
  //           <Grid container spacing={3}>
  //             <Grid item xs={12} sm={6}>
  //               <div className="info-label">Personal Phone Number</div>
  //               <div className="info-value">{displayValue(d.personal_phone)}</div>
  //             </Grid>
  //             <Grid item xs={12} sm={6}>
  //               <div className="info-label">Personal Email Address</div>
  //               <div className="info-value">{displayValue(d.personal_email)}</div>
  //             </Grid>
  //             <Grid item xs={12} sm={6}>
  //               <div className="info-label">Emergency Contact Name</div>
  //               <div className="info-value">
  //                 {displayValue(d.emergency_contact_name)}
  //               </div>
  //             </Grid>
  //             <Grid item xs={12} sm={6}>
  //               <div className="info-label">Emergency Contact No.</div>
  //               <div className="info-value">
  //                 {displayValue(d.emergency_contact_number)}
  //               </div>
  //             </Grid>
  //             <Grid item xs={12} sm={6}>
  //               <div className="info-label">Present Address</div>
  //               <div className="info-value">
  //                 {displayValue(d.present_address)}
  //               </div>
  //             </Grid>
  //             <Grid item xs={12} sm={6}>
  //               <div className="info-label">Permanent Address</div>
  //               <div className="info-value">
  //                 {displayValue(d.permanent_address)}
  //               </div>
  //             </Grid>
  //           </Grid>
  //         </div>

  //         {/* Identity Information */}
  //         <div className="info-section">
  //           <h3 className="section-header">Identity Information</h3>
  //           <Grid container spacing={3}>
  //             <Grid item xs={12} sm={6}>
  //               <div className="info-label">UAN</div>
  //               <div className="info-value">{displayValue(d.uan)}</div>
  //             </Grid>
  //             <Grid item xs={12} sm={6}>
  //               <div className="info-label">PAN</div>
  //               <div className="info-value">{displayValue(d.pan)}</div>
  //             </Grid>
  //             <Grid item xs={12} sm={6}>
  //               <div className="info-label">Aadhaar</div>
  //               <div className="info-value">{displayValue(d.aadhaar)}</div>
  //             </Grid>
  //           </Grid>
  //         </div>
  //       </div>
  //     );
  //   };

  return (
    <div className="hrms-page">
      {/* Tabs */}
      <div className="hrms-tabs">
        <div className="tab-buttons">
          <button
            className={`tab-btn ${
              hrmsTopTab === "overview" && !viewAll ? "active" : ""
            }`}
            onClick={() => {
              setHrmsTopTab("overview");
              setViewAll(false);
              setActiveTab("Profile");
              setShowEmployeeOverview(false);
              //   setSelectedEmpForOverview(null);
            }}
          >
            My Overview
          </button>
          {!isHrmsClient && (
            <button
              type="button"
              className={`tab-btn ${hrmsTopTab === "dashboard" ? "active" : ""}`}
              onClick={() => {
                setHrmsTopTab("dashboard");
                setViewAll(false);
                setShowEmployeeOverview(false);
              }}
            >
              Dashboard
            </button>
          )}
          {(isAdmin || isManager || isTeamLeaveApprover) && (
            <button
              className={`tab-btn ${viewAll && !showEmployeeOverview ? "active" : ""}`}
              onClick={() => {
                setHrmsTopTab("overview");
                setViewAll(true);
                setSelectedEmpId(null);
                setShowEmployeeOverview(false);
                // setSelectedEmpForOverview(null);
              }}
            >
              All Employees
            </button>
          )}
          {selectedEmpForOverview && (
            <button
              className={`tab-btn ${showEmployeeOverview ? "active" : ""}`}
              onClick={() => {
                setHrmsTopTab("overview");
                setViewAll(true);
                setShowEmployeeOverview(true);
              }}
            >
              {selectedEmpForOverview.first_name} overview
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedEmpForOverview(null);
                  setShowEmployeeOverview(false);
                }}
                style={{
                  marginLeft: "8px",
                  cursor: "pointer",
                  fontWeight: "bold",
                }}
              >
                ✕
              </span>
            </button>
          )}
        </div>
      </div>

      {hrmsTopTab === "dashboard" && !isHrmsClient && (
        <HrmsDashboard isAdmin={isHrmsAdmin} />
      )}

      {hrmsTopTab === "overview" && (
        <>
          {/* Employee Card */}
          {!viewAll && (
            <div className="employee-card">
              <div className="card-header">
                <div className="profile-section">
                  <div className="profile-image">
                    <div
                      className="profile-pic profile-pic--initial"
                      aria-hidden
                    >
                      {initialFromEmployee(data)}
                    </div>
                  </div>
                  <div className="profile-info">
                    <div className="employee-id">
                      Employee ID: {displayValue(data.company_employee_id || data.employeeID)}
                    </div>

                    <div className="employee-name">
                      {displayValue(data.first_name)}{" "}
                      {displayValue(data.last_name)}
                    </div>
                    <div className="employee-designation">
                      Designation: {displayValue(data.designation)}
                    </div>
                    <div className="employee-manager">
                      Manager: {getManagerDisplayName(data.manager_id)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* All Employees inline panel (for admin/manager) - only show if NOT viewing employee overview */}
          {viewAll && !showEmployeeOverview && (isAdmin || isManager || isTeamLeaveApprover) && (
            <div className="employee-table-panel panel">
              <div style={{ padding: 16 }}>
                <div style={{ display: "flex", paddingBottom: 16 }}>
                  <div className="dash-search" style={{ flex: 1 }}>
                    <input
                      type="text"
                      placeholder="Search Employee"
                      aria-label="Search Employee"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <div style={{ width: 16 }} />
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span className="status-indicator">
                      <span className="status-dot status-active" /> Active
                    </span>
                    <span style={{ width: 12 }} />
                    <span className="status-indicator">
                      <span className="status-dot status-inactive" /> Inactive
                    </span>
                  </div>
                </div>

                <Table
                  title=""
                  header={employeeHeader}
                  hiddenFields={["_rawData"]}
                  data={filteredActiveEmployees.map((emp) => {
                    const openOverview = () => {
                      setSelectedEmpForOverview(emp);
                      setViewAll(true);
                      setShowEmployeeOverview(true);
                    };
                    const clickProps = {
                      onClick: openOverview,
                      style: { cursor: "pointer" },
                    };

                    return {
                      firstName: (
                        <span
                          className="employee-name-with-status"
                          {...clickProps}
                        >
                          {emp.first_name}
                          <span
                            className={`status-dot ${emp.status ? "status-active" : "status-inactive"}`}
                          />
                        </span>
                      ),
                      lastName: <span {...clickProps}>{emp.last_name}</span>,
                      companyEmployeeId: (
                        <span {...clickProps}>
                          {emp.company_employee_id || emp.employeeID || "—"}
                        </span>
                      ),
                      email: <span {...clickProps}>{emp.email || "—"}</span>,
                      phone: <span {...clickProps}>{emp.phone || "—"}</span>,
                      role: (
                        <span {...clickProps}>
                          {emp.role || emp.designation || "Employee"}
                        </span>
                      ),
                      doj: (
                        <span {...clickProps}>
                          {emp.doj ? dateFormatter(emp.doj) : "—"}
                        </span>
                      ),
                      _rawData: emp,
                    };
                  })}
                  aligncss="dashboard-table employee-table"
                  className="employee-table-scope"
                  actions={isAdmin || isManager ? ["edit"] : []}
                  onActionClick={handleAllEmployeesAction}
                />
              </div>
            </div>
          )}

          {/* Selected Employee Overview Tab (replaces list when employee is selected) */}
          {selectedEmpForOverview && viewAll && showEmployeeOverview && (
            <>
              {/* Employee Card */}
              <div className="employee-card">
                <div className="card-header">
                  <div className="profile-section">
                    <div className="profile-image">
                      <div
                        className="profile-pic profile-pic--initial"
                        aria-hidden
                      >
                        {initialFromEmployee(selectedEmpForOverview)}
                      </div>
                    </div>
                    <div className="profile-info">
                      <div className="employee-id">
                        Employee ID:{" "}
                        {displayValue(
                          selectedEmpForOverview.company_employee_id ||
                            selectedEmpForOverview.employeeID,
                        )}
                      </div>
                      <div className="employee-name">
                        {displayValue(selectedEmpForOverview.first_name)}{" "}
                        {displayValue(selectedEmpForOverview.last_name)}
                      </div>
                      <div className="employee-designation">
                        Designation:{" "}
                        {displayValue(selectedEmpForOverview.designation)}
                      </div>
                      <div className="employee-manager">
                        Manager:{" "}
                        {managerNameMap[
                          String(selectedEmpForOverview.manager_id)
                        ] ||
                          displayValue(
                            selectedEmpForOverview.manager_name ||
                              selectedEmpForOverview.manager,
                          )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Overview Content */}
              <div className="employee-overview-panel">
                {renderEmployeeOverview(selectedEmpForOverview)}
              </div>
            </>
          )}

          {/* Action Tabs and Profile Content (hidden when viewing All Employees) */}
          {!viewAll && (
            <>
              <div className="action-tabs">
                {[
                  "Profile",
                  "Login",
                  "Leave",
                  "Performance Index",
                  // "Related Data",
                  // "Resignation",
                ].map((tab) => (
                  <button
                    key={tab}
                    className={`action-tab ${activeTab === tab ? "active" : ""}`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {/* Profile Tab Content */}
              {activeTab === "Profile" && (
                <div className="profile-content">
                  <div
                    style={{
                      marginBottom: "16px",
                      display: "flex",
                      justifyContent: "flex-end",
                      width: "100%",
                      gap: "8px",
                    }}
                  >
                    <Button
                      type="button"
                      icon={isEmployeeEditMode ? <CloseIcon /> : <Edit />}
                      className="section-edit-btn"
                      text={isEmployeeEditMode ? "Cancel" : "Edit"}
                      handleClick={() =>
                        isEmployeeEditMode
                          ? handleEditCancel()
                          : handleEditClick(data)
                      }
                    />
                    {isEmployeeEditMode && (
                      <Button
                        type="button"
                        className="section-edit-btn"
                        icon={<Save />}
                        text={savingProfile ? "Saving…" : "Update"}
                        disable={savingProfile}
                        handleClick={() => handleHrmsProfileSave(data)}
                      />
                    )}
                  </div>
                  {/* Basic Information */}
                  <div className="info-section">
                    <div className="section-header-row">
                      <h3 className="section-header">Basic information</h3>
                      {/* <button
                    type="button"
                    className="section-edit-btn"
                    onClick={() =>
                      isEmployeeEditMode
                        ? handleEditCancel()
                        : handleEditClick(data)
                    }
                  >
                    {isEmployeeEditMode ? "Cancel" : "Edit"}
                  </button> */}
                    </div>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <div className="info-label">Employee ID</div>
                        <div className="info-value">
                          {renderEditableInfoValueWithRole(
                            "company_employee_id",
                            data.company_employee_id || data.employeeID,
                            "basicInfo",
                          )}
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <div className="info-label">Email ID</div>
                        <div className="info-value">
                          {renderEditableInfoValueWithRole(
                            "email",
                            data.email,
                            "basicInfo",
                          )}
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <div className="info-label">First Name</div>
                        <div className="info-value">
                          {renderEditableInfoValueWithRole(
                            "first_name",
                            data.first_name,
                            "basicInfo",
                          )}
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <div className="info-label">Last Name</div>
                        <div className="info-value">
                          {renderEditableInfoValueWithRole(
                            "last_name",
                            data.last_name,
                            "basicInfo",
                          )}
                        </div>
                      </Grid>
                    </Grid>
                  </div>

                  {/* Work Information */}
                  <div className="info-section">
                    <h3 className="section-header">Work information</h3>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <div className="info-label">Department</div>
                        <div className="info-value">
                          {renderEditableInfoValueWithRole(
                            "department",
                            data.department,
                            "workInfo",
                          )}
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <div className="info-label">Location</div>
                        <div className="info-value">
                          {renderEditableInfoValueWithRole(
                            "location",
                            data.location,
                            "workInfo",
                          )}
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <div className="info-label">Reporting Manager</div>
                        <div className="info-value">
                          {renderEditableInfoValueWithRole(
                            "manager_id",
                            data.manager_id,
                            "workInfo",
                            { type: "select", dropdown: managerDropdown },
                          )}
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <div className="info-label">Employment Type</div>
                        <div className="info-value">
                          {renderEditableInfoValueWithRole(
                            "employment_type",
                            data.employment_type,
                            "workInfo",
                          )}
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <div className="info-label">Date of Joining</div>
                        <div className="info-value">
                          {renderEditableInfoValueWithRole(
                            "doj",
                            data.doj,
                            "workInfo",
                            {
                              type: "date",
                            },
                          )}
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <div className="info-label">Designation</div>
                        <div className="info-value">
                          {renderEditableInfoValueWithRole(
                            "designation",
                            data.designation,
                            "workInfo",
                          )}
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <div className="info-label">Branch Office</div>
                        <div className="info-value">
                          {renderEditableInfoValueWithRole(
                            "branch_office",
                            data.branch_office,
                            "workInfo",
                            { type: "select", dropdown: branchOfficeDropdown },
                          )}
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <div className="info-label">Work Phone</div>
                        <div className="info-value">
                          {renderEditableInfoValueWithRole(
                            "phone",
                            data.phone,
                            "workInfo",
                          )}
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <div className="info-label">Meeting Link</div>
                        <div className="info-value">
                          {renderEditableInfoValueWithRole(
                            "meeting_link",
                            data.meeting_link,
                            "workInfo",
                          )}
                        </div>
                      </Grid>
                    </Grid>
                  </div>

                  {/* Personal Details */}
                  <div className="info-section">
                    <h3 className="section-header">Personal Details</h3>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <div className="info-label">Date of Birth</div>
                        <div className="info-value">
                          {renderEditableInfoValueWithRole(
                            "date_of_birth",
                            data.date_of_birth,
                            "personalDetails",
                            { type: "date" },
                          )}
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <div className="info-label">Gender</div>
                        <div className="info-value">
                          {renderEditableInfoValueWithRole(
                            "gender",
                            data.gender,
                            "personalDetails",
                          )}
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <div className="info-label">Nationality</div>
                        <div className="info-value">
                          {renderEditableInfoValueWithRole(
                            "nationality",
                            data.nationality,
                            "personalDetails",
                          )}
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <div className="info-label">Marital Status</div>
                        <div className="info-value">
                          {renderEditableInfoValueWithRole(
                            "marital_status",
                            data.marital_status,
                            "personalDetails",
                          )}
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <div className="info-label">Blood Group</div>
                        <div className="info-value">
                          {renderEditableInfoValueWithRole(
                            "blood_group",
                            data.blood_group,
                            "personalDetails",
                          )}
                        </div>
                      </Grid>
                    </Grid>
                  </div>

                  {/* Contact Details */}
                  <div className="info-section">
                    <h3 className="section-header">Contact Details</h3>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <div className="info-label">Personal Phone</div>
                        <div className="info-value">
                          {renderEditableInfoValueWithRole(
                            "personal_phone",
                            data.personal_phone,
                            "contactDetails",
                          )}
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <div className="info-label">Personal Email Address</div>
                        <div className="info-value">
                          {renderEditableInfoValueWithRole(
                            "personal_email",
                            data.personal_email,
                            "contactDetails",
                          )}
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <div className="info-label">Emergency Contact Name</div>
                        <div className="info-value">
                          {renderEditableInfoValueWithRole(
                            "emergency_contact_name",
                            data.emergency_contact_name,
                            "contactDetails",
                          )}
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <div className="info-label">Emergency Contact No.</div>
                        <div className="info-value">
                          {renderEditableInfoValueWithRole(
                            "emergency_contact_number",
                            data.emergency_contact_number,
                            "contactDetails",
                          )}
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <div className="info-label">Present Address</div>
                        <div className="info-value">
                          {renderEditableInfoValueWithRole(
                            "present_address",
                            data.present_address,
                            "contactDetails",
                          )}
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <div className="info-label">Permanent Address</div>
                        <div className="info-value">
                          {renderEditableInfoValueWithRole(
                            "permanent_address",
                            data.permanent_address,
                            "contactDetails",
                          )}
                        </div>
                      </Grid>
                    </Grid>
                  </div>

                  {/* Identity Information */}
                  <div className="info-section">
                    <h3 className="section-header">Identity Information</h3>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <div className="info-label">UAN</div>
                        <div className="info-value">
                          {renderEditableInfoValueWithRole(
                            "uan",
                            data.uan,
                            "identityInfo",
                          )}
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <div className="info-label">PAN</div>
                        <div className="info-value">
                          {renderEditableInfoValueWithRole(
                            "pan",
                            data.pan,
                            "identityInfo",
                          )}
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <div className="info-label">Aadhaar</div>
                        <div className="info-value">
                          {renderEditableInfoValueWithRole(
                            "aadhaar",
                            data.aadhaar,
                            "identityInfo",
                          )}
                        </div>
                      </Grid>
                    </Grid>
                  </div>
                </div>
              )}

              {activeTab === "Performance Index" && (
                <div className="profile-content">
                  {isAdmin ? (
                    <PerformanceIndex />
                  ) : (
                    <PerformanceIndex
                      scopeEmployeeId={loggedEmployeeId}
                      hideSearchControls={true}
                    />
                  )}
                </div>
              )}

              {activeTab === "Leave" && (
                <div className="profile-content">
                  <Leaves />
                </div>
              )}
              {activeTab === "Login" && (
                <div className="profile-content">
                  <WorkingHours />
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
